import React, { useState, useEffect } from 'react';
import { purchaseOrdersAPI, jobsAPI, contractorsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Edit, Send, ShoppingCart, Trash2, Download, Eye, X, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  acknowledged: 'status-in_progress',
  completed: 'status-completed',
  cancelled: 'status-cancelled',
};

const InvoiceMatchBadge = ({ match }) => {
  if (!match || !match.status) {
    return <span className="text-[11px] text-muted-foreground">Awaiting invoice</span>;
  }
  const map = {
    matched: { label: '✅ Matches PO', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    overcharge: { label: `⚠ Over by $${Math.abs(match.variance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, cls: 'bg-red-500/20 text-red-400 border-red-500/40' },
    under: { label: 'Under PO', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
    unknown: { label: 'Invoice — needs check', cls: 'bg-amber-400/15 text-amber-300 border-amber-400/30' },
  };
  const m = map[match.status] || map.unknown;
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${m.cls}`} title={match.invoice_total != null ? `Invoice $${match.invoice_total} vs PO $${match.po_total}` : ''}>{m.label}</span>;
};

export const PurchaseOrdersPage = () => {
  const [pos, setPos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [sendDialog, setSendDialog] = useState({ open: false, po: null });
  const [sendData, setSendData] = useState({ email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [tradeLink, setTradeLink] = useState('');
  const [linkExpiry, setLinkExpiry] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [formData, setFormData] = useState({
    job_id: '',
    contractor_id: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    delivery_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [posRes, jobsRes, contractorsRes] = await Promise.all([
        purchaseOrdersAPI.getAll(),
        jobsAPI.getAll(),
        contractorsAPI.getAll()
      ]);
      setPos(posRes.data);
      setJobs(jobsRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await purchaseOrdersAPI.create(formData);
      toast.success('Purchase Order created');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleStatusChange = async (poId, newStatus) => {
    try {
      await purchaseOrdersAPI.update(poId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setFormData({
      job_id: '',
      contractor_id: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      delivery_date: '',
    });
  };

  const handleDownloadPdf = async (poId, poNumber) => {
    try {
      const response = await purchaseOrdersAPI.downloadPdf(poId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${poNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (poId, poNumber) => {
    try {
      const response = await purchaseOrdersAPI.downloadPdf(poId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Purchase Order ${poNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const getJobTitle = (jobId) => jobs.find(j => j.id === jobId)?.title || 'Unknown';
  const getContractorName = (contractorId) => contractors.find(c => c.id === contractorId)?.name || 'Unknown';

  const openSendDialog = async (po) => {
    const contractor = contractors.find(c => c.id === po.contractor_id);
    setSendData({ email: contractor?.email || '', message: '' });
    setTradeLink('');
    setLinkExpiry('');
    setSendDialog({ open: true, po });
    try {
      const res = await purchaseOrdersAPI.getTradeLink(po.id);
      setTradeLink(res.data.upload_link);
      setLinkExpiry(res.data.expires_at || '');
    } catch (e) { /* link optional */ }
  };

  const handleRegenerate = async () => {
    if (!sendDialog.po) return;
    setRegenerating(true);
    try {
      const res = await purchaseOrdersAPI.regenerateLink(sendDialog.po.id);
      setTradeLink(res.data.upload_link);
      setLinkExpiry(res.data.expires_at || '');
      toast.success('New link generated — the old one no longer works');
    } catch (e) {
      toast.error('Failed to regenerate link');
    } finally {
      setRegenerating(false);
    }
  };

  const handleSendTrade = async () => {
    if (!sendData.email.trim()) { toast.error('Enter the trade\'s email'); return; }
    setSending(true);
    try {
      const res = await purchaseOrdersAPI.sendTrade(sendDialog.po.id, sendData);
      if (res.data.email_delivered === false) {
        toast.warning(res.data.warning || `Couldn't email ${sendData.email} — copy the link and share it manually.`);
      } else {
        toast.success(`PO sent to ${sendData.email} with an invoice-upload link`);
        setSendDialog({ open: false, po: null });
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send PO');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!tradeLink) return;
    navigator.clipboard?.writeText(tradeLink).then(
      () => toast.success('Upload link copied'),
      () => toast.info(tradeLink)
    );
  };

  const filteredPos = pos.filter(p =>
    p.po_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Purchase Orders</h1>
          <p className="text-muted-foreground mt-1">Manage orders to contractors</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="font-semibold rounded-lg" data-testid="add-po-btn">
              <Plus className="h-4 w-4 mr-2" />
              New PO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Job *</Label>
                  <Select value={formData.job_id} onValueChange={(v) => setFormData({ ...formData, job_id: v })}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="po-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Contractor *</Label>
                  <Select value={formData.contractor_id} onValueChange={(v) => setFormData({ ...formData, contractor_id: v })}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="po-contractor">
                      <SelectValue placeholder="Select a contractor" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {contractors.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} - {c.trade_type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  className="bg-input border-border rounded-lg"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-lg">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2 border border-border rounded-lg p-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="col-span-6 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="col-span-3 bg-input border-border rounded-lg text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="col-span-1"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-border">
                    <p className="font-mono font-bold">
                      Subtotal: ${calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-lg"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                <Button type="submit" className="font-semibold rounded-lg" data-testid="po-submit">
                  Create PO
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search purchase orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-lg"
          data-testid="po-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredPos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mb-4" />
              <p>No purchase orders found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>PO #</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Delivery</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPos.map((po) => (
                  <TableRow key={po.id} className="border-border" data-testid={`po-row-${po.id}`}>
                    <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                    <TableCell>{getJobTitle(po.job_id)}</TableCell>
                    <TableCell>{getContractorName(po.contractor_id)}</TableCell>
                    <TableCell className="font-mono">
                      ${po.total?.toFixed(2)}
                      <div className="mt-1"><InvoiceMatchBadge match={po.invoice_match} /></div>
                    </TableCell>
                    <TableCell>
                      {po.delivery_date ? format(new Date(po.delivery_date), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={po.status} onValueChange={(v) => handleStatusChange(po.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[po.status]} rounded-lg text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="acknowledged">Acknowledged</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(po.id, po.po_number)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(po.id, po.po_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send PO to trade (with invoice-upload link)" onClick={() => openSendDialog(po)} data-testid={`send-po-${po.id}`}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-800">{pdfTitle}</h3>
              <div className="flex items-center gap-2">
                <a 
                  href={pdfUrl} 
                  download={`${pdfTitle}.pdf`}
                  className="inline-flex items-center px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </a>
                <Button variant="ghost" size="icon" onClick={() => { setPdfViewerOpen(false); window.URL.revokeObjectURL(pdfUrl); }} className="text-gray-600 hover:text-gray-800">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-gray-200 overflow-hidden">
              <object 
                data={pdfUrl} 
                type="application/pdf"
                className="w-full h-full"
              >
                <div className="flex flex-col items-center justify-center h-full text-gray-600">
                  <p className="mb-4">Unable to display PDF in browser.</p>
                  <a 
                    href={pdfUrl} 
                    download={`${pdfTitle}.pdf`}
                    className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                  >
                    Download PDF
                  </a>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}

      {/* Send PO to trade dialog */}
      <Dialog open={sendDialog.open} onOpenChange={(o) => setSendDialog({ open: o, po: o ? sendDialog.po : null })}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" /> Send PO to trade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We'll email <span className="font-medium text-foreground">{sendDialog.po?.po_number}</span> to your trade with a secure
              link so they can upload their invoice straight back to this job — no login needed.
            </p>
            <div className="space-y-2">
              <Label>Trade email</Label>
              <Input
                type="email"
                value={sendData.email}
                onChange={(e) => setSendData({ ...sendData, email: e.target.value })}
                placeholder="trade@example.com"
                className="bg-input border-border rounded-lg"
                data-testid="send-po-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                value={sendData.message}
                onChange={(e) => setSendData({ ...sendData, message: e.target.value })}
                placeholder="Add a note for the trade…"
                rows={3}
                className="bg-input border-border rounded-lg"
              />
            </div>
            {tradeLink && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-muted-foreground">Or share this link directly:</p>
                  {linkExpiry && (
                    <span className="text-[11px] text-muted-foreground" data-testid="link-expiry">
                      Expires {format(new Date(linkExpiry), 'd MMM yyyy')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs truncate flex-1 text-primary">{tradeLink}</code>
                  <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-trade-link">Copy</Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="mt-2 h-7 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="regenerate-link-btn"
                >
                  {regenerating
                    ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Generating…</>
                    : <><RefreshCw className="h-3 w-3 mr-1.5" /> Regenerate link (invalidates the old one)</>}
                </Button>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendDialog({ open: false, po: null })}>Cancel</Button>
              <Button onClick={handleSendTrade} disabled={sending} data-testid="send-po-confirm">
                {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send PO</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
